// src/components/reporting/ReviewConversations.js - SuperAdmin-only report for reviewing tenant conversations
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';
import { useApiCall } from '../../hooks/useApiCall';
import '../../styles/globals.css';
import './Reporting.css';

const formatCompactDate = (dateString) => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleDateString();
};

const ReviewConversations = ({
  tenant,
  onNavigateBack,
  onViewConversationDetail,
  breadcrumbs = [],
  initialSelectedUserId = null,
  initialSelectedUserData = null
}) => {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(initialSelectedUserId);
  const [selectedUserData, setSelectedUserData] = useState(initialSelectedUserData);
  const [conversations, setConversations] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleteConfirmTitle, setDeleteConfirmTitle] = useState(null);
  const { loading, error, makeRequest } = useApiCall();

  const loadUsers = useCallback(async () => {
    if (!tenant?.id) return;
    try {
      const data = await makeRequest(
        () => apiService.get(`/api/v1/reports/tenantusers/${tenant.id}`, user),
        'Loading users...'
      );
      if (data?.success) {
        setUsers(data.data.users || []);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  }, [tenant?.id, user, makeRequest]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (initialSelectedUserId && user) {
      handleViewUser(initialSelectedUserId, initialSelectedUserData);
    }
    // Only run on mount
  }, []);

  const handleViewUser = async (userId, userData) => {
    setSelectedUserId(userId);
    setSelectedUserData(userData);
    setConversations(null);
    try {
      const data = await makeRequest(
        () => apiService.get(`/api/v1/reports/userconversations/${userId}`, user),
        'Loading conversations...'
      );
      if (data?.success) {
        setConversations(data.data.conversations || []);
      }
    } catch (err) {
      console.error('Error loading conversations:', err);
    }
  };

  const handleBackToUsers = () => {
    setSelectedUserId(null);
    setSelectedUserData(null);
    setConversations(null);
  };

  const handleDeleteClick = (conversationId, conversationTitle) => {
    setDeleteConfirmId(conversationId);
    setDeleteConfirmTitle(conversationTitle);
  };

  const handleDeleteConfirm = async () => {
    try {
      await makeRequest(
        () => apiService.delete(`/api/v1/conversations/${deleteConfirmId}`, user),
        'Deleting conversation...'
      );
      setConversations(prev => prev.filter(c => c.id !== deleteConfirmId));
    } catch (err) {
      console.error('Error deleting conversation:', err);
    } finally {
      setDeleteConfirmId(null);
      setDeleteConfirmTitle(null);
    }
  };

  if (!tenant) {
    return (
      <div className="review-conversations">
        <div className="alert alert-error">
          <strong>Error:</strong> No tenant selected.
        </div>
      </div>
    );
  }

  return (
    <div className="review-conversations">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-nav mb-4">
        <nav className="flex items-center space-x-2 text-sm text-gray-600">
          {breadcrumbs && breadcrumbs.length > 0 ? (
            breadcrumbs.map((breadcrumb, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                )}
                {breadcrumb.active ? (
                  <span className="text-gray-900 font-medium">{breadcrumb.label}</span>
                ) : (
                  <button onClick={breadcrumb.onClick} className="text-blue-600 hover:text-blue-800 hover:underline">
                    {breadcrumb.label}
                  </button>
                )}
              </React.Fragment>
            ))
          ) : (
            <span className="text-gray-900 font-medium">Review Conversations</span>
          )}
        </nav>
      </div>

      {/* Header */}
      <div className="header-section mb-6">
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={selectedUserId ? handleBackToUsers : onNavigateBack}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            title="Back"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {selectedUserId
                ? `Conversations: ${selectedUserData?.displayName || selectedUserData?.email || 'User'}`
                : 'Review Conversations'}
            </h2>
            <p className="text-gray-600 text-sm">
              {selectedUserId
                ? `${selectedUserData?.email || ''} — click a title to inspect, or delete to remove permanently`
                : `Users in ${tenant.name} ordered by conversation count`}
            </p>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* View A: Users list */}
      {!selectedUserId && !loading && users && (
        <div className="card">
          {users.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No active users found for this tenant.</p>
          ) : (
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Active Conversations</th>
                    <th>Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleViewUser(u.id, u)}>
                      <td className="font-medium text-gray-900">{u.displayName || '—'}</td>
                      <td className="text-gray-600">{u.email}</td>
                      <td>
                        <span className="message-badge">{u.conversationCount || 0}</span>
                      </td>
                      <td className="text-gray-600">{formatCompactDate(u.lastActivity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* View B: Conversations list */}
      {selectedUserId && !loading && conversations && (
        <div className="card">
          {conversations.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No conversations found for this user.</p>
          ) : (
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Last Activity</th>
                    <th>Type</th>
                    <th>Methodology</th>
                    <th>Messages</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {conversations.map(c => (
                    <tr key={c.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onViewConversationDetail(c.id, c.title, selectedUserId, selectedUserData)}>
                      <td className="font-medium text-gray-900">{c.title || 'Untitled'}</td>
                      <td className="text-gray-600">{formatCompactDate(c.lastActivity)}</td>
                      <td className="text-gray-600">{c.type || '—'}</td>
                      <td className="text-gray-600">{c.methodology || '—'}</td>
                      <td>
                        <span className="message-badge">{c.messageCount || 0}</span>
                      </td>
                      <td className="text-gray-600">
                        {c.status === 'archived' ? 'Archived' : 'Active'}
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteClick(c.id, c.title)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirmId && (
        <div className="modal-overlay" onClick={() => { setDeleteConfirmId(null); setDeleteConfirmTitle(null); }}>
          <div className="modal-card card" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Delete Conversation</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to permanently delete <strong>&quot;{deleteConfirmTitle}&quot;</strong>? This cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                className="btn btn-secondary"
                onClick={() => { setDeleteConfirmId(null); setDeleteConfirmTitle(null); }}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ReviewConversations.propTypes = {
  tenant: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    slug: PropTypes.string
  }).isRequired,
  onNavigateBack: PropTypes.func.isRequired,
  onViewConversationDetail: PropTypes.func.isRequired,
  breadcrumbs: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    active: PropTypes.bool
  })),
  initialSelectedUserId: PropTypes.string,
  initialSelectedUserData: PropTypes.object
};

export default ReviewConversations;
